import { FC, useEffect } from 'react';
import classNames from 'classnames';
import { Link, router } from '@inertiajs/react';

import { AppModalProps } from '@/types/modal.type';
import { ModalView } from '@/Components/ModalView';
import { CloseSvg } from '@/Components/Icons/CloseSvg';
import { CreateReelVideoSvg } from '@/Components/Icons/CreateReelVideoSvg';
import { CreateReelAudioSvg } from '@/Components/Icons/CreateReelAudioSvg';
import { CreateReelScriptSvg } from '@/Components/Icons/CreateReelScriptSvg';
import { ChevronRightSvg } from '@/Components/Icons/ChevronRightSvg';
import { MODAL_QUERY_ARG } from '@/types/query.type';
import { useQueryParam } from '@/hook/useQueryParam';
import { useDataState } from '@/hook/useDataState';
import { LOADING_DATA_STATE, SUCCESS_DATA_STATE } from '@/types/data.type';
import { CameraSquareSvg } from '@/Components/Icons/CameraSquareSvg';
import { useInteractiveCallback } from '@/hook/useInteractiveCallback';
import { LoadingSvg } from '@/Components/Icons/LoadingSvg';
import { useToast } from '@/hook/useToast';
import { CartSvg } from '@/Components/Icons/CartSvg';

type CreateReelModalProps = AppModalProps<{
    userId: number;
    workspaceId: number;
    workspaceName: string;
    forTesting: boolean;
    firstPersonaId: number | null;
    features: {
        createButton: boolean;
        personas: boolean;
        marketplacePage: boolean;
    };
}>;

export const CreateReelModal: FC<CreateReelModalProps> = ({
    payload,
    onClose,
}) => {
    const firstPersonaId = payload?.firstPersonaId;
    const features = payload?.features;

    const isStandInEnabled = features?.personas;
    const isMarketplaceEnabled = features?.marketplacePage;

    const [modalId, setModalId] = useQueryParam({ name: MODAL_QUERY_ARG });
    const requestState = useDataState();
    const { showToast } = useToast();

    const isRequested = requestState.dataState === SUCCESS_DATA_STATE;
    const isRequesting = requestState.dataState === LOADING_DATA_STATE;

    useEffect(() => {
        return () => {
            setModalId(null);
        };
    }, []);

    const onRequestStandIn = useInteractiveCallback(() => {
        if (isRequesting || isRequested) {
            return;
        }

        requestState.setLoading();

        const reqPayload = {
            user_id: payload?.userId,
            workspace_id: payload?.workspaceId,
            workspace_name: payload?.workspaceName,
            for_testing: payload?.forTesting,
            button_label: 'Request access to Stand-in',
            environment_url: window.location.origin,
        };

        const isDevTest = false;

        if (isDevTest) {
            setTimeout(() => {
                requestState.setSuccess();
            }, 3000);

            return;
        }

        fetch(`https://hooks.zapier.com/hooks/catch/18870477/ubiaowf/`, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
            },
            body: JSON.stringify(reqPayload),
        })
            .then(function () {
                requestState.setSuccess();

                // onClose();

                // showToast({
                //     variant: 'success',
                //     label: `Great! We'll be in touch shortly`,
                // });
            })
            .catch(function (ex) {
                requestState.setInitial();

                onClose();

                showToast({
                    variant: 'danger',
                    label: `Something went wrong`,
                });
            });
    });

    const options = [];

    options.push({
        isStandIn: false,
        isAddon: false,
        route: route('reels.uploadFlyover'),
        title: 'Upload an existing video...',
        text: 'Upload files to add videos to your Library and use in Flows.',
        bgClassName: 'group-hover:scale-125 scale-150',
        contentClassName: 'group-hover:scale-110',
        bgElement: (
            <img
                src="/img/shade-bg-1.svg"
                className="w-full h-full object-cover "
            />
        ),
        contentElement: <CreateReelVideoSvg />,
    });

    if (isStandInEnabled) {
        options.push({
            isStandIn: false,
            isAddon: false,
            route: firstPersonaId
                ? route('stand-ins.show', {
                      stand_in: firstPersonaId,
                      active_tab: 'upload',
                  })
                : route('stand-ins.index'),
            title: 'Generate video with an AI Stand-in',
            text: 'Provide recorded speech or text to generate new video content, starring your AI Stand-In.',
            bgClassName: 'group-hover:scale-75',
            contentClassName: 'group-hover:scale-110',
            bgElement: (
                <img
                    src="/img/shade-bg-e.svg"
                    style={{
                        transform: 'scale(2.6, 2.6) translate(0 , 0)',
                    }}
                    className="w-full h-full object-cover"
                />
            ),
            contentElement: <CreateReelAudioSvg />,
        });

        if (isMarketplaceEnabled) {
            options.push({
                isStandIn: false,
                isAddon: false,
                route: route('marketplace'),
                title: 'Find someone to help you...',
                text: 'Work with a certified video partners from our Marketplace to create your content',
                bgClassName: 'group-hover:scale-100 scale-125',
                contentClassName: 'group-hover:scale-110',
                bgElement: (
                    <img
                        src="/img/shade-bg-a.svg"
                        className="w-full h-full object-cover "
                    />
                ),
                contentElement: <CartSvg className="translate-y-2" />,
            });
        }
    } else {
        options.push({
            isStandIn: false,
            isAddon: true,
            isLoading: isRequesting,
            isDisabled: isRequesting || isRequested,
            route: null,
            title: isRequested
                ? 'Great! We’ll be in touch shortly'
                : 'Magically create realistic video!',
            text: isRequested
                ? 'Our team will reach out within a day to discuss adding an AI powered Video Stand-in to your Workspace.'
                : 'Trained on your likeness, use AI to instantly and securely generate realistic new video from a script or audio recording!',
            bgClassName: 'group-hover:scale-125 scale-150',
            contentClassName: 'group-hover:scale-110',
            containerClassName: 'bg-teal-500/20',
            bgElement: (
                <div
                    className="w-full h-full object-cover "
                    style={{
                        backgroundColor: isRequested
                            ? 'rgba(249, 158, 0, 1)'
                            : 'rgba(16, 181, 168, 1)',
                    }}
                />
            ),
            contentElement: <CameraSquareSvg width={58} />,
            onClick: onRequestStandIn,
        });
    }

    return (
        <ModalView
            maxWidth="xl"
            onClose={onClose}
            contentContainerClassName="py-6 text-brand-ocean flex flex-col justify-center items-center">
            <div className="flex flex-row items-center w-full px-6">
                <div className="flex-1 text-heading-four !font-bold text-brand-ocean text-left">
                    Create Video Reel
                </div>
                <button
                    title="Close"
                    aria-label="Close"
                    className="p-2 -mr-1 transition-all rounded-md hover:cursor-pointer hover:bg-neutral-mid"
                    onClick={onClose}>
                    <CloseSvg className="[&_path]:stroke-1 [&_path]:stroke-brand-ocean" />
                </button>
            </div>
            <div className="flex flex-col gap-y-3 mt-2 mx-4">
                {options.map((option, index) => {
                    return (
                        <Link
                            aria-label={option.title}
                            tabIndex={0}
                            key={option.title}
                            // TODO: Revisit. Not urgent though
                            // @ts-expect-error
                            href={option.route}
                            onClick={(event) => {
                                if (option.onClick) {
                                    event.preventDefault();
                                    option.onClick();
                                    return;
                                }

                                if (option.route) {
                                    router.visit(option.route);
                                }

                                onClose();
                            }}
                            className={classNames(
                                'group flex flex-row items-center hover:cursor-pointer hover:bg-neutral-mid px-2 py-2 transition-all duration-300 rounded-lg select-none',
                                option.containerClassName,
                                {
                                    'pointer-events-none': option.isDisabled,
                                },
                            )}>
                            <div className="relative min-w-36 min-h-20 rounded-md overflow-hidden flex items-center justify-center border border-gray-20">
                                <div
                                    className={classNames(
                                        'transition-all duration-300 flex-1 absolute w-full h-full',
                                        option.bgClassName,
                                    )}>
                                    {option.bgElement}
                                </div>
                                <div
                                    className={classNames(
                                        'transition-all duration-300 z-10',
                                        option.contentClassName,
                                    )}>
                                    {option.contentElement}
                                </div>
                            </div>
                            <div className="ml-4">
                                <div className="flex flex-row flex-wrap gap-x-2 items-center mb-2">
                                    <div className="text-body-text-15 text-brand-ocean">
                                        {option.title}
                                    </div>
                                    <div
                                        className={classNames(
                                            'py-1 px-2 text-small-text-10 rounded-full block',
                                            {
                                                '!hidden ': !option.isStandIn,
                                            },
                                        )}
                                        style={{
                                            backgroundColor:
                                                'rgba(214, 206, 235, 1)',
                                            color: 'rgba(82, 39, 204, 1)',
                                        }}>
                                        AI Stand-In
                                    </div>
                                    <div
                                        className={classNames(
                                            'py-1 px-2 text-small-text-10 rounded-full text-white block',
                                            {
                                                '!hidden ': !option.isAddon,
                                            },
                                        )}
                                        style={{
                                            backgroundColor:
                                                'rgba(16, 181, 168, 1)',
                                        }}>
                                        Add-on
                                    </div>
                                    <div
                                        className={classNames(
                                            'opacity-0 flex flex-row items-center justify-center text-small-text-10 text-gray-60',
                                            {
                                                '!opacity-100':
                                                    option.isLoading,
                                                hidden: option.isStandIn,
                                            },
                                        )}>
                                        <LoadingSvg
                                            width={18}
                                            height={18}
                                            className="animate-rotate-steps mr-0.5"
                                        />
                                        {/* Requesting... */}
                                    </div>
                                </div>
                                <div className="text-small-text-13 text-gray-80">
                                    {option.text}
                                </div>
                            </div>
                            <div
                                className={classNames('ml-4 mr-2', {
                                    'opacity-0 ': option.isAddon,
                                })}>
                                <ChevronRightSvg
                                    className="stroke-1 stroke-gray-80 opacity-80"
                                    width={10}
                                    height={10}
                                    fill="rgba(130, 130, 130, 1)"
                                />
                            </div>
                        </Link>
                    );
                })}
            </div>
        </ModalView>
    );
};
